import styled from '@emotion/styled';
import { faVideo } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslations } from 'use-intl';

import { Button } from '@/shared-ui';

const ProgressBar = styled.div<{ width: string }>`
  width: ${(props) => props.width};
  transform: translateZ(0);
`;

interface Props {
  uploadProgress: number | null;
  isStalled?: boolean;
  onRetry?: () => void;
}

export default function ProjectUsabilityResponseSaving({
  uploadProgress,
  isStalled = false,
  onRetry,
}: Props) {
  const t = useTranslations('Usability');

  const progressPercentage = uploadProgress ?? 0;
  const showProgress = uploadProgress !== null;

  return (
    <div
      className='fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4'
      data-test='recording-finalization-overlay'
    >
      <div className='flex w-full max-w-md flex-col items-center rounded-3xl bg-white px-5 py-6 text-center text-blue-950 shadow-2xl sm:px-8'>
        <FontAwesomeIcon icon={faVideo} className='text-5xl' />
        <p className='mt-4 text-lg'>{t('savingRecording')}</p>

        {isStalled && (
          <>
            <p
              className='mt-4 text-sm text-amber-700'
              data-test='upload-stalled-body'
            >
              {t('uploadStalledBody')}
            </p>
            <Button
              className='mt-4'
              onClick={onRetry}
              data-test='upload-retry-button'
            >
              {t('retryUpload')}
            </Button>
          </>
        )}

        {!isStalled && (
          <>
            <div className='mt-4 w-full'>
              {showProgress ? (
                <div className='flex flex-col gap-2'>
                  <div className='relative h-1 w-full overflow-hidden rounded-full bg-gray-200'>
                    <ProgressBar
                      className='absolute top-0 left-0 h-full rounded-full bg-blue-950 transition-all duration-300 ease-out'
                      data-test='upload-progress-bar'
                      data-progress={progressPercentage}
                      width={`${progressPercentage}%`}
                    />
                  </div>
                  <p className='text-xs text-gray-600'>
                    {t('percentComplete', {
                      percent: Math.round(progressPercentage),
                    })}
                  </p>
                </div>
              ) : (
                <div className='flex flex-col gap-2'>
                  <div className='relative h-1 w-full overflow-hidden rounded-full bg-gray-200'>
                    <div className='absolute top-0 left-0 h-full w-3/10 animate-pulse rounded-full bg-blue-950' />
                  </div>
                  <p className='text-xs text-gray-600'>
                    {t('preparingUpload')}
                  </p>
                </div>
              )}
            </div>

            <p className='mt-4 text-sm text-gray-600'>{t('savingBody')}</p>
          </>
        )}
      </div>
    </div>
  );
}
